import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import PageHero from "../components/common/PageHero";
import Reveal from "../components/motion/Reveal";
import { Stagger, StaggerItem } from "../components/motion/Stagger";

const INFO = [
  { icon: BusinessRoundedIcon, labelKey: "company", value: "URAMAKS.com", color: "#4f7cff" },
  { icon: EmailRoundedIcon, labelKey: "email", value: "support@uramaks.com", href: "mailto:support@uramaks.com", color: "#22d3ee" },
  { icon: LanguageRoundedIcon, labelKey: "website", value: "uramaks.com", href: "https://uramaks.com", color: "#a855f7" },
];

function ContactsPage() {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  return (
    <Box>
      <PageHero eyebrow={t("contactsEyebrow")} title={t("contactsTitle")} subtitle={t("contactsLead")} />

      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 7 }, pb: 4 }}>
        <Grid container spacing={4}>
          {/* Info cards */}
          <Grid item xs={12} md={5}>
            <Stagger>
              <Stack spacing={2}>
                {INFO.map(({ icon: Icon, labelKey, value, href, color }) => (
                  <StaggerItem key={labelKey}>
                    <Card
                      sx={{
                        "&:hover": { transform: "translateX(6px)", borderColor: "primary.main" },
                      }}
                    >
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2.5,
                              display: "grid",
                              placeItems: "center",
                              color: "#fff",
                              background: `linear-gradient(135deg, ${color}, ${color}99)`,
                              boxShadow: `0 10px 24px -12px ${color}`,
                            }}
                          >
                            <Icon />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary">
                              {t(labelKey)}
                            </Typography>
                            {href ? (
                              <Typography
                                component="a"
                                href={href}
                                target={href.startsWith("http") ? "_blank" : undefined}
                                rel="noreferrer"
                                sx={{
                                  display: "block",
                                  fontWeight: 600,
                                  color: "text.primary",
                                  textDecoration: "none",
                                  "&:hover": { color: "primary.main" },
                                }}
                              >
                                {value}
                              </Typography>
                            ) : (
                              <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
                            )}
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </StaggerItem>
                ))}
              </Stack>
            </Stagger>
          </Grid>

          {/* Contact form */}
          <Grid item xs={12} md={7}>
            <Reveal direction="left">
              <Card>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    {t("formTitle")}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    {t("formSubtitle")}
                  </Typography>

                  {sent && (
                    <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
                      {t("formSuccess")}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label={t("formName")} variant="outlined" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth required type="email" label={t("formEmail")} variant="outlined" />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label={t("formSubject")} variant="outlined" />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth required multiline minRows={4} label={t("formMessage")} variant="outlined" />
                      </Grid>
                      <Grid item xs={12}>
                        <Button type="submit" size="large" variant="contained" endIcon={<SendRoundedIcon />}>
                          {t("formSubmit")}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Reveal>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default ContactsPage;
